import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Card, H2, Icon } from '@blueprintjs/core';
import { useNavigate } from 'react-router-dom';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { subscribeToActHistory } from '../firebase/actService';

const CHOOSER_IDS = [1, 2, 3, 4];

function randomChoice(list) {
  return list[Math.floor(Math.random() * list.length)];
}

function normalizePlayerName(value) {
  return typeof value === 'string' ? value.trim().toLowerCase() : '';
}

function collectEntryPlayers(entry) {
  const playerSet = new Set();

  Object.values(entry?.teams || {}).forEach((team) => {
    Object.values(team?.players || {}).forEach((playerName) => {
      const trimmedName = typeof playerName === 'string' ? playerName.trim() : '';
      if (trimmedName) {
        playerSet.add(trimmedName);
      }
    });
  });

  Object.values(entry?.races || {}).forEach((cupRaces) => {
    Object.values(cupRaces || {}).forEach((race) => {
      Object.values(race || {}).forEach((raceData) => {
        const trimmedName = typeof raceData?.racer === 'string' ? raceData.racer.trim() : '';
        if (trimmedName && trimmedName !== 'A' && trimmedName !== 'B' && trimmedName !== 'C') {
          playerSet.add(trimmedName);
        }
      });
    });
  });

  return [...playerSet];
}

function extractRaceParticipants(entry, race) {
  return Object.entries(race || {})
    .map(([teamKey, raceData]) => {
      const rawPlayerName = typeof raceData?.racer === 'string' ? raceData.racer.trim() : '';
      const rosterPlayerName = entry?.teams?.[teamKey]?.players?.[rawPlayerName];
      const playerName =
        rawPlayerName === 'A' || rawPlayerName === 'B' || rawPlayerName === 'C'
          ? typeof rosterPlayerName === 'string'
            ? rosterPlayerName.trim()
            : ''
          : rawPlayerName;
      const place = Number(raceData?.place);

      return {
        playerName,
        place,
      };
    })
    .filter(({ playerName, place }) => playerName && Number.isFinite(place));
}

function getPlayerOptions(history) {
  const uniquePlayers = new Set();

  history.forEach((entry) => {
    collectEntryPlayers(entry).forEach((playerName) => uniquePlayers.add(playerName));
  });

  return [...uniquePlayers].sort((a, b) => a.localeCompare(b));
}

function findDisplayName(playerName, playerOptions) {
  const normalizedName = normalizePlayerName(playerName);
  if (!normalizedName) {
    return '';
  }

  return (
    playerOptions.find((option) => normalizePlayerName(option) === normalizedName) ||
    playerName.trim()
  );
}

function getTeamPlayers(entry, teamKey) {
  return Object.values(entry?.teams?.[teamKey]?.players || {})
    .map((playerName) => (typeof playerName === 'string' ? playerName.trim() : ''))
    .filter(Boolean);
}

function getTeamOutcome(entry, teamKey) {
  const rankings = ['A', 'B', 'C', 'D']
    .map((team) => ({
      team,
      totalPoints: Number(entry?.totals?.[team]) || 0,
    }))
    .sort((a, b) => b.totalPoints - a.totalPoints);

  const maxPoints = rankings[0]?.totalPoints ?? 0;
  const winnerTeams = new Set(
    rankings.filter((teamRanking) => teamRanking.totalPoints === maxPoints).map(({ team }) => team)
  );
  const swapTeams = new Set(rankings.slice(0, 2).map(({ team }) => team));

  return {
    won: winnerTeams.has(teamKey),
    swapped: swapTeams.has(teamKey),
  };
}

function ChooserTool() {
  const [tiles, setTiles] = useState(() => CHOOSER_IDS.map((id) => ({ id, enabled: true })));
  const [rollingId, setRollingId] = useState(null);
  const [winnerId, setWinnerId] = useState(null);
  const [isSpinning, setIsSpinning] = useState(false);
  const [error, setError] = useState('');
  const [resultText, setResultText] = useState('');
  const [hasPicked, setHasPicked] = useState(false);

  const tileRefs = useRef({});
  const audioCtxRef = useRef(null);
  const confettiCanvasRef = useRef(null);
  const confettiCtxRef = useRef(null);
  const confettiParticlesRef = useRef([]);
  const confettiRafRef = useRef(0);

  const eligibleIds = useMemo(
    () => tiles.filter((tile) => tile.enabled).map((tile) => tile.id),
    [tiles]
  );

  useEffect(() => {
    const canvas = confettiCanvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    confettiCtxRef.current = ctx;

    const resize = () => {
      const dpr = Math.max(1, window.devicePixelRatio || 1);
      const width = window.innerWidth;
      const height = window.innerHeight;
      canvas.width = Math.floor(width * dpr);
      canvas.height = Math.floor(height * dpr);
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    resize();
    window.addEventListener('resize', resize);

    return () => {
      window.removeEventListener('resize', resize);
      if (confettiRafRef.current) {
        cancelAnimationFrame(confettiRafRef.current);
      }
    };
  }, []);

  const ensureAudioContext = () => {
    if (audioCtxRef.current) return audioCtxRef.current;
    const AudioContextClass = window.AudioContext || window.webkitAudioContext;
    if (!AudioContextClass) return null;
    try {
      audioCtxRef.current = new AudioContextClass();
      return audioCtxRef.current;
    } catch {
      return null;
    }
  };

  const playTick = () => {
    const ctx = ensureAudioContext();
    if (!ctx) return;

    if (ctx.state === 'suspended') {
      ctx.resume().catch(() => {});
    }

    const now = ctx.currentTime;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();

    osc.type = 'square';
    osc.frequency.setValueAtTime(880 + Math.random() * 160, now);

    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.exponentialRampToValueAtTime(0.02, now + 0.003);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.03);

    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start(now);
    osc.stop(now + 0.035);
  };

  const animateConfetti = () => {
    confettiRafRef.current = 0;

    const ctx = confettiCtxRef.current;
    const canvas = confettiCanvasRef.current;
    if (!ctx || !canvas) return;

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    const particles = confettiParticlesRef.current;
    for (let i = particles.length - 1; i >= 0; i--) {
      const p = particles[i];
      p.vx *= p.drag;
      p.vy = p.vy * p.drag + p.gravity;
      p.x += p.vx;
      p.y += p.vy;
      p.rot += p.vrot;
      p.life -= 1;

      const offscreen =
        p.y > window.innerHeight + 20 ||
        p.x < -20 ||
        p.x > window.innerWidth + 20 ||
        p.life <= 0;

      if (offscreen) {
        particles.splice(i, 1);
        continue;
      }

      ctx.save();
      ctx.translate(p.x, p.y);
      ctx.rotate(p.rot);
      ctx.fillStyle = p.color;
      if (p.shape === 'dot') {
        ctx.beginPath();
        ctx.arc(0, 0, p.size * 0.45, 0, Math.PI * 2);
        ctx.fill();
      } else {
        ctx.fillRect(-p.size / 2, -p.size / 3, p.size, p.size * 0.66);
      }
      ctx.restore();
    }

    if (particles.length > 0) {
      confettiRafRef.current = requestAnimationFrame(animateConfetti);
    }
  };

  const burstConfettiForTile = (id) => {
    const tileEl = tileRefs.current[id];
    if (!tileEl) return;

    const rect = tileEl.getBoundingClientRect();
    const x = rect.left + rect.width / 2;
    const y = rect.top + rect.height / 2;
    const colors = ['#60a5fa', '#6ee7b7', '#fbbf24', '#f472b6', '#fca5a5', '#c4b5fd'];
    const particles = confettiParticlesRef.current;
    const count = 110;

    for (let i = 0; i < count; i++) {
      const angle = (Math.PI * 2 * i) / count + (Math.random() * 0.35 - 0.175);
      const speed = 2.2 + Math.random() * 4.2;
      particles.push({
        x,
        y,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed - (2.1 + Math.random() * 1.5),
        gravity: 0.12 + Math.random() * 0.07,
        drag: 0.992,
        life: 52 + Math.floor(Math.random() * 28),
        size: 4 + Math.random() * 4,
        rot: Math.random() * Math.PI * 2,
        vrot: (Math.random() - 0.5) * 0.28,
        color: colors[Math.floor(Math.random() * colors.length)],
        shape: Math.random() < 0.75 ? 'rect' : 'dot',
      });
    }

    if (!confettiRafRef.current) {
      confettiRafRef.current = requestAnimationFrame(animateConfetti);
    }
  };

  const runRouletteAnimation = async (eligibleList, durationMs = 1500) => {
    const prefersReduced =
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const duration = prefersReduced ? 450 : durationMs;
    const start = performance.now();
    let lastId = null;

    return new Promise((resolve) => {
      const step = () => {
        const elapsed = performance.now() - start;
        const progress = Math.min(elapsed / duration, 1);

        let nextId = randomChoice(eligibleList);
        if (eligibleList.length > 1 && nextId === lastId) {
          const idx = eligibleList.indexOf(nextId);
          nextId = eligibleList[(idx + 1) % eligibleList.length];
        }

        lastId = nextId;
        setRollingId(nextId);
        playTick();

        if (progress >= 1) {
          resolve();
          return;
        }

        const delay = Math.round(40 + (150 - 40) * progress * progress);
        window.setTimeout(step, delay);
      };

      step();
    });
  };

  const handlePick = async () => {
    if (isSpinning) return;

    setError('');
    setResultText('');
    setWinnerId(null);
    setRollingId(null);

    const eligibleList = tiles.filter((tile) => tile.enabled).map((tile) => tile.id);
    if (eligibleList.length < 2) {
      setError('Select at least 2.');
      return;
    }

    // Fair pick: choose uniformly from the current eligible list.
    const finalWinnerId = randomChoice(eligibleList);

    setIsSpinning(true);
    setResultText('Picking...');

    try {
      await runRouletteAnimation(eligibleList, 1500);
    } finally {
      setIsSpinning(false);
    }

    setRollingId(null);
    setWinnerId(finalWinnerId);
    setHasPicked(true);
    setResultText(`Winner: ${finalWinnerId} (removed from next round)`);
    setTiles((prev) =>
      prev.map((tile) => (tile.id === finalWinnerId ? { ...tile, enabled: false } : tile))
    );

    window.setTimeout(() => burstConfettiForTile(finalWinnerId), 0);
  };

  const handleReset = () => {
    if (isSpinning) return;
    setTiles(CHOOSER_IDS.map((id) => ({ id, enabled: true })));
    setRollingId(null);
    setWinnerId(null);
    setError('');
    setResultText('');
    setHasPicked(false);
  };

  return (
    <Card
      style={{
        padding: '1rem',
        position: 'relative',
        overflow: 'hidden',
        background:
          'radial-gradient(circle at 12% 5%, rgba(96,165,250,0.10), transparent 45%), radial-gradient(circle at 90% 0%, rgba(110,231,183,0.08), transparent 40%), var(--bg-secondary)',
      }}
    >
      <canvas
        ref={confettiCanvasRef}
        aria-hidden="true"
        style={{
          position: 'fixed',
          inset: 0,
          pointerEvents: 'none',
          zIndex: 1000,
        }}
      />

      <div style={{ marginBottom: '0.75rem' }}>
        <h3 style={{ margin: 0, marginBottom: '0.25rem' }}>Chooser</h3>
        <p style={{ margin: 0, color: 'var(--text-secondary)' }}>
          Press Pick to choose a winner. Winners are automatically removed until you reset.
        </p>
      </div>

      <div
        role="group"
        aria-label="Chooser numbers"
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
          gap: '0.75rem',
          maxWidth: '520px',
          margin: '0 auto 1rem',
        }}
      >
        {tiles.map((tile) => {
          const isEligible = tile.enabled;
          const isRolling = rollingId === tile.id;
          const isWinner = winnerId === tile.id;

          return (
            <div
              key={tile.id}
              ref={(el) => {
                tileRefs.current[tile.id] = el;
              }}
              role="status"
              aria-label={`Number ${tile.id}, ${isEligible ? 'eligible' : 'out'}${
                isWinner ? ', winner' : ''
              }`}
              style={{
                position: 'relative',
                minHeight: '120px',
                borderRadius: '16px',
                border: isWinner
                  ? '2px solid rgba(110,231,183,0.9)'
                  : isRolling
                  ? '2px solid rgba(96,165,250,0.8)'
                  : '1px solid var(--border)',
                background: isWinner
                  ? 'linear-gradient(180deg, rgba(110,231,183,0.20), rgba(110,231,183,0.08))'
                  : isRolling
                  ? 'linear-gradient(180deg, rgba(96,165,250,0.20), rgba(96,165,250,0.08))'
                  : 'linear-gradient(180deg, rgba(255,255,255,0.04), rgba(255,255,255,0.02))',
                boxShadow: isWinner
                  ? '0 0 0 2px rgba(110,231,183,0.35), 0 0 26px rgba(110,231,183,0.30)'
                  : isRolling
                  ? '0 0 0 2px rgba(96,165,250,0.25), 0 0 18px rgba(96,165,250,0.20)'
                  : 'inset 0 1px 0 rgba(255,255,255,0.03)',
                color: 'var(--text-primary)',
                fontSize: '2.25rem',
                fontWeight: 700,
                cursor: 'default',
                opacity: isEligible || isWinner ? 1 : 0.35,
                transform: isRolling ? 'scale(1.02)' : 'scale(1)',
                transition: 'all 140ms ease',
                width: '100%',
                display: 'grid',
                placeItems: 'center',
              }}
            >
              {tile.id}

              {isWinner && (
                <span
                  style={{
                    position: 'absolute',
                    top: '8px',
                    right: '8px',
                    fontSize: '0.65rem',
                    fontWeight: 800,
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                    padding: '0.25rem 0.45rem',
                    borderRadius: '999px',
                    background: 'linear-gradient(180deg, #a7f3d0, #6ee7b7)',
                    color: '#062014',
                  }}
                >
                  Winner
                </span>
              )}

              {!isEligible && !isWinner && (
                <span
                  aria-hidden="true"
                  style={{
                    position: 'absolute',
                    bottom: '8px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    fontSize: '0.7rem',
                    color: 'var(--text-secondary)',
                    letterSpacing: '0.06em',
                    textTransform: 'uppercase',
                  }}
                >
                  Out
                </span>
              )}
            </div>
          );
        })}
      </div>

      <div
        style={{
          display: 'grid',
          justifyItems: 'center',
          gap: '0.5rem',
        }}
      >
        <button
          type="button"
          onClick={handlePick}
          disabled={isSpinning}
          style={{
            border: 0,
            borderRadius: '12px',
            padding: '0.85rem 1.25rem',
            minWidth: '180px',
            cursor: isSpinning ? 'not-allowed' : 'pointer',
            fontWeight: 700,
            color: '#06111d',
            background: 'linear-gradient(180deg, #93c5fd, #60a5fa)',
            boxShadow: '0 8px 18px rgba(96,165,250,0.25)',
            opacity: isSpinning ? 0.8 : 1,
          }}
        >
          {hasPicked ? 'Pick Again' : 'Pick'}
        </button>

        <button
          type="button"
          onClick={handleReset}
          disabled={isSpinning}
          style={{
            border: 'none',
            background: 'transparent',
            color: '#bfdbfe',
            textDecoration: 'underline',
            cursor: isSpinning ? 'not-allowed' : 'pointer',
            fontSize: '0.9rem',
            padding: 0,
          }}
        >
          Reset
        </button>

        <div style={{ color: 'var(--text-secondary)', fontSize: '0.85rem' }} aria-live="polite">
          {eligibleIds.length} remaining
        </div>

        <div
          style={{
            minHeight: '1.2rem',
            color: 'var(--error)',
            fontSize: '0.9rem',
          }}
          role="alert"
          aria-live="assertive"
        >
          {error}
        </div>

        <div
          style={{
            minHeight: '1.2rem',
            color: '#d1fae5',
            fontSize: '0.9rem',
            fontWeight: 600,
          }}
          aria-live="polite"
        >
          {resultText}
        </div>

      </div>
    </Card>
  );
}

function VersusTool({ history }) {
  const [playerOne, setPlayerOne] = useState('');
  const [playerTwo, setPlayerTwo] = useState('');

  const playerOptions = useMemo(() => getPlayerOptions(history), [history]);

  const comparison = useMemo(() => {
    const normalizedPlayerOne = normalizePlayerName(playerOne);
    const normalizedPlayerTwo = normalizePlayerName(playerTwo);

    if (!normalizedPlayerOne || !normalizedPlayerTwo || normalizedPlayerOne === normalizedPlayerTwo) {
      return null;
    }

    let racesAgainst = 0;
    let playerOneWins = 0;
    let playerTwoWins = 0;
    history.forEach((entry) => {
      Object.values(entry?.races || {}).forEach((cupRaces) => {
        Object.values(cupRaces || {}).forEach((race) => {
          const participants = extractRaceParticipants(entry, race);
          const playerOneRace = participants.find(
            ({ playerName }) => normalizePlayerName(playerName) === normalizedPlayerOne
          );
          const playerTwoRace = participants.find(
            ({ playerName }) => normalizePlayerName(playerName) === normalizedPlayerTwo
          );

          if (!playerOneRace || !playerTwoRace) {
            return;
          }

          racesAgainst += 1;

          if (playerOneRace.place < playerTwoRace.place) {
            playerOneWins += 1;
          } else if (playerTwoRace.place < playerOneRace.place) {
            playerTwoWins += 1;
          }
        });
      });
    });

    return {
      racesAgainst,
      playerOneWins,
      playerTwoWins,
    };
  }, [history, playerOne, playerTwo]);

  const normalizedPlayerOne = normalizePlayerName(playerOne);
  const normalizedPlayerTwo = normalizePlayerName(playerTwo);
  const isSamePlayer =
    normalizedPlayerOne &&
    normalizedPlayerTwo &&
    normalizedPlayerOne === normalizedPlayerTwo;
  const playerOneLeads =
    comparison && comparison.playerOneWins > comparison.playerTwoWins;
  const playerTwoLeads =
    comparison && comparison.playerTwoWins > comparison.playerOneWins;

  return (
    <Card
      style={{
        padding: '1rem',
        background:
          'radial-gradient(circle at 0% 0%, rgba(96,165,250,0.08), transparent 35%), radial-gradient(circle at 100% 0%, rgba(52,211,153,0.08), transparent 35%), var(--bg-secondary)',
      }}
    >
      <div style={{ marginBottom: '1rem' }}>
        <h3 style={{ margin: 0, marginBottom: '0.25rem' }}>Versus</h3>
        <p style={{ margin: 0, color: 'var(--text-secondary)' }}>
          Compare two racers across ACT history and see who finished ahead more often.
        </p>
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
          gap: '0.75rem',
          marginBottom: '1rem',
        }}
      >
        <label style={{ display: 'grid', gap: '0.35rem' }}>
          <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>Player 1</span>
          <input
            list="versus-player-options"
            value={playerOne}
            onChange={(event) => setPlayerOne(event.target.value)}
            placeholder="Enter first player"
            style={{
              width: '100%',
              padding: '0.75rem 0.9rem',
              borderRadius: '10px',
              border: '1px solid var(--border)',
              background: 'var(--bg-primary)',
              color: 'var(--text-primary)',
            }}
          />
        </label>

        <label style={{ display: 'grid', gap: '0.35rem' }}>
          <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>Player 2</span>
          <input
            list="versus-player-options"
            value={playerTwo}
            onChange={(event) => setPlayerTwo(event.target.value)}
            placeholder="Enter second player"
            style={{
              width: '100%',
              padding: '0.75rem 0.9rem',
              borderRadius: '10px',
              border: '1px solid var(--border)',
              background: 'var(--bg-primary)',
              color: 'var(--text-primary)',
            }}
          />
        </label>
      </div>

      <datalist id="versus-player-options">
        {playerOptions.map((playerName) => (
          <option key={playerName} value={playerName} />
        ))}
      </datalist>

      {isSamePlayer ? (
        <div style={{ color: 'var(--warning)', fontSize: '0.95rem' }}>
          Pick two different players.
        </div>
      ) : comparison ? (
        comparison.racesAgainst > 0 ? (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))',
              gap: '0.75rem',
            }}
          >
            {[
              {
                label: 'Races Against',
                value: comparison.racesAgainst,
                percentage: '',
                tone: 'neutral',
              },
              {
                label: `${playerOne || 'Player 1'} Wins`,
                value: comparison.playerOneWins,
                percentage: `${Math.round((comparison.playerOneWins / comparison.racesAgainst) * 100)}%`,
                tone: playerOneLeads ? 'winner' : playerTwoLeads ? 'loser' : 'neutral',
              },
              {
                label: `${playerTwo || 'Player 2'} Wins`,
                value: comparison.playerTwoWins,
                percentage: `${Math.round((comparison.playerTwoWins / comparison.racesAgainst) * 100)}%`,
                tone: playerTwoLeads ? 'winner' : playerOneLeads ? 'loser' : 'neutral',
              },
            ].map((item) => (
              <div
                key={item.label}
                style={{
                  position: 'relative',
                  padding: '0.85rem 0.95rem',
                  borderRadius: '12px',
                  border:
                    item.tone === 'winner'
                      ? '1px solid rgba(74, 222, 128, 0.35)'
                      : item.tone === 'loser'
                      ? '1px solid rgba(248, 113, 113, 0.30)'
                      : '1px solid var(--border)',
                  background:
                    item.tone === 'winner'
                      ? 'linear-gradient(180deg, rgba(74, 222, 128, 0.10), rgba(255,255,255,0.03))'
                      : item.tone === 'loser'
                      ? 'linear-gradient(180deg, rgba(248, 113, 113, 0.08), rgba(255,255,255,0.03))'
                      : 'rgba(255,255,255,0.03)',
                  boxShadow:
                    item.tone === 'winner'
                      ? '0 0 18px rgba(74, 222, 128, 0.10)'
                      : item.tone === 'loser'
                      ? '0 0 18px rgba(248, 113, 113, 0.08)'
                      : 'none',
                }}
              >
                <div
                  style={{
                    fontSize: '0.75rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.06em',
                    color: 'var(--text-secondary)',
                    marginBottom: '0.4rem',
                  }}
                >
                  {item.label}
                </div>
                <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: '0.75rem' }}>
                  <div style={{ fontSize: '1.45rem', fontWeight: 700 }}>{item.value}</div>
                  {item.percentage ? (
                    <div
                      style={{
                        fontSize: '0.82rem',
                        color: 'var(--text-secondary)',
                        opacity: 0.85,
                        fontWeight: 600,
                      }}
                    >
                      {item.percentage}
                    </div>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div style={{ color: 'var(--text-secondary)', fontSize: '0.95rem' }}>
            No head-to-head races found for those two players.
          </div>
        )
      ) : (
        <div style={{ color: 'var(--text-secondary)', fontSize: '0.95rem' }}>
          Enter two names to compare their head-to-head results.
        </div>
      )}
    </Card>
  );
}

function OpponentPerformanceTooltip({ active, payload, label, selectedPlayerLabel }) {
  if (!active || !payload?.length) {
    return null;
  }

  const total = payload.reduce((sum, item) => sum + (Number(item.value) || 0), 0);

  return (
    <div
      style={{
        background: 'rgba(15, 23, 42, 0.96)',
        border: '1px solid rgba(148, 163, 184, 0.25)',
        borderRadius: '12px',
        padding: '0.75rem 0.9rem',
        boxShadow: '0 16px 40px rgba(2, 6, 23, 0.28)',
      }}
    >
      <div style={{ fontWeight: 700, marginBottom: '0.45rem', color: '#f8fafc' }}>{label}</div>
      {payload.map((item) => (
        <div
          key={item.dataKey}
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: '0.9rem',
            color: '#cbd5e1',
            fontSize: '0.9rem',
          }}
        >
          <span>{item.name}</span>
          <span style={{ fontWeight: 700, color: item.color }}>{item.value}</span>
        </div>
      ))}
      <div
        style={{
          marginTop: '0.45rem',
          paddingTop: '0.45rem',
          borderTop: '1px solid rgba(148, 163, 184, 0.18)',
          color: '#e2e8f0',
          fontSize: '0.86rem',
        }}
      >
        {selectedPlayerLabel} vs {label}: {total} races
      </div>
    </div>
  );
}

function OpponentPerformanceTool({ history }) {
  const [selectedPlayer, setSelectedPlayer] = useState('');

  const playerOptions = useMemo(() => getPlayerOptions(history), [history]);
  const normalizedSelectedPlayer = normalizePlayerName(selectedPlayer);
  const selectedPlayerLabel = findDisplayName(selectedPlayer, playerOptions);

  const opponentPerformance = useMemo(() => {
    if (!normalizedSelectedPlayer) {
      return [];
    }

    const opponentMap = new Map();

    history.forEach((entry) => {
      Object.values(entry?.races || {}).forEach((cupRaces) => {
        Object.values(cupRaces || {}).forEach((race) => {
          const participants = extractRaceParticipants(entry, race);
          const selectedPlayerRace = participants.find(
            ({ playerName }) => normalizePlayerName(playerName) === normalizedSelectedPlayer
          );

          if (!selectedPlayerRace) {
            return;
          }

          participants.forEach(({ playerName, place }) => {
            const normalizedOpponent = normalizePlayerName(playerName);

            if (
              !normalizedOpponent ||
              normalizedOpponent === normalizedSelectedPlayer ||
              !Number.isFinite(place) ||
              place === selectedPlayerRace.place
            ) {
              return;
            }

            const existing = opponentMap.get(normalizedOpponent) || {
              opponent: playerName,
              wins: 0,
              losses: 0,
              total: 0,
            };

            if (
              normalizePlayerName(existing.opponent) !== normalizedOpponent ||
              existing.opponent.length < playerName.length
            ) {
              existing.opponent = playerName;
            }

            if (selectedPlayerRace.place < place) {
              existing.wins += 1;
            } else {
              existing.losses += 1;
            }

            existing.total = existing.wins + existing.losses;
            opponentMap.set(normalizedOpponent, existing);
          });
        });
      });
    });

    return [...opponentMap.values()].sort(
      (a, b) => b.total - a.total || b.wins - a.wins || a.opponent.localeCompare(b.opponent)
    );
  }, [history, normalizedSelectedPlayer]);

  const chartWidth = Math.max(520, opponentPerformance.length * 78);

  return (
    <Card
      style={{
        padding: '1rem',
        background:
          'radial-gradient(circle at 0% 100%, rgba(34,197,94,0.09), transparent 36%), radial-gradient(circle at 100% 0%, rgba(239,68,68,0.08), transparent 34%), var(--bg-secondary)',
      }}
    >
      <div style={{ marginBottom: '1rem' }}>
        <h3 style={{ margin: 0, marginBottom: '0.25rem' }}>Performance By Opponent</h3>
        <p style={{ margin: 0, color: 'var(--text-secondary)' }}>
          Enter one racer to see their head-to-head record against every individual opponent.
        </p>
      </div>

      <label style={{ display: 'grid', gap: '0.35rem', marginBottom: '1rem' }}>
        <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>Player</span>
        <input
          list="opponent-performance-player-options"
          value={selectedPlayer}
          onChange={(event) => setSelectedPlayer(event.target.value)}
          placeholder="Enter a player name"
          style={{
            width: '100%',
            padding: '0.75rem 0.9rem',
            borderRadius: '10px',
            border: '1px solid var(--border)',
            background: 'var(--bg-primary)',
            color: 'var(--text-primary)',
          }}
        />
      </label>

      <datalist id="opponent-performance-player-options">
        {playerOptions.map((playerName) => (
          <option key={playerName} value={playerName} />
        ))}
      </datalist>

      {!normalizedSelectedPlayer ? (
        <div style={{ color: 'var(--text-secondary)', fontSize: '0.95rem' }}>
          Enter one name to chart that player&apos;s results against each opponent.
        </div>
      ) : opponentPerformance.length === 0 ? (
        <div style={{ color: 'var(--text-secondary)', fontSize: '0.95rem' }}>
          No opponent data found for {selectedPlayerLabel || selectedPlayer.trim()}.
        </div>
      ) : (
        <>
          <div
            style={{
              overflowX: 'auto',
              paddingBottom: '0.25rem',
            }}
          >
            <div style={{ width: `${chartWidth}px`, minWidth: '100%', height: '360px' }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={opponentPerformance}
                  margin={{ top: 8, right: 12, left: 0, bottom: 68 }}
                  barGap={4}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(148, 163, 184, 0.14)" />
                  <XAxis
                    dataKey="opponent"
                    interval={0}
                    angle={-35}
                    textAnchor="end"
                    height={76}
                    tick={{ fontSize: 11, fill: 'var(--text-secondary)' }}
                  />
                  <YAxis
                    allowDecimals={false}
                    tick={{ fontSize: 11, fill: 'var(--text-secondary)' }}
                    label={{
                      value: 'Head-to-Head Races',
                      angle: -90,
                      position: 'insideLeft',
                      style: { fill: 'var(--text-secondary)', fontSize: '0.8rem' },
                    }}
                  />
                  <Tooltip
                    content={
                      <OpponentPerformanceTooltip selectedPlayerLabel={selectedPlayerLabel} />
                    }
                  />
                  <Legend />
                  <Bar dataKey="wins" name="Wins" stackId="results" fill="#22c55e" radius={[0, 0, 6, 6]} />
                  <Bar dataKey="losses" name="Losses" stackId="results" fill="#ef4444" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </>
      )}
    </Card>
  );
}

function BestPartnerTool({ history }) {
  const [selectedPlayer, setSelectedPlayer] = useState('');

  const playerOptions = useMemo(() => getPlayerOptions(history), [history]);
  const normalizedSelectedPlayer = normalizePlayerName(selectedPlayer);
  const selectedPlayerLabel = findDisplayName(selectedPlayer, playerOptions);

  const partnerStats = useMemo(() => {
    if (!normalizedSelectedPlayer) {
      return [];
    }

    const partnerMap = new Map();

    history.forEach((entry) => {
      ['A', 'B', 'C', 'D'].forEach((teamKey) => {
        const teamPlayers = getTeamPlayers(entry, teamKey);
        const selectedOnTeam = teamPlayers.some(
          (playerName) => normalizePlayerName(playerName) === normalizedSelectedPlayer
        );

        if (!selectedOnTeam) {
          return;
        }

        const teammates = teamPlayers.filter(
          (playerName) => normalizePlayerName(playerName) !== normalizedSelectedPlayer
        );

        if (teammates.length === 0) {
          return;
        }

        const teamOutcome = getTeamOutcome(entry, teamKey);

        teammates.forEach((teammateName) => {
          const normalizedTeammate = normalizePlayerName(teammateName);
          if (!normalizedTeammate) {
            return;
          }

          const existing = partnerMap.get(normalizedTeammate) || {
            partner: teammateName,
            actsTogether: 0,
            winsTogether: 0,
            swapsTogether: 0,
          };

          if (
            normalizePlayerName(existing.partner) !== normalizedTeammate ||
            existing.partner.length < teammateName.length
          ) {
            existing.partner = teammateName;
          }

          existing.actsTogether += 1;
          if (teamOutcome.won) {
            existing.winsTogether += 1;
          }
          if (teamOutcome.swapped) {
            existing.swapsTogether += 1;
          }

          partnerMap.set(normalizedTeammate, existing);
        });
      });
    });

    return [...partnerMap.values()].sort(
      (a, b) =>
        b.winsTogether - a.winsTogether ||
        b.swapsTogether - a.swapsTogether ||
        b.actsTogether - a.actsTogether ||
        a.partner.localeCompare(b.partner)
    );
  }, [history, normalizedSelectedPlayer]);

  const topWinPartner = useMemo(() => {
    if (!partnerStats.length) {
      return null;
    }

    return [...partnerStats].sort(
      (a, b) =>
        b.winsTogether - a.winsTogether ||
        b.actsTogether - a.actsTogether ||
        b.swapsTogether - a.swapsTogether ||
        a.partner.localeCompare(b.partner)
    )[0];
  }, [partnerStats]);

  const topSwapPartner = useMemo(() => {
    if (!partnerStats.length) {
      return null;
    }

    return [...partnerStats].sort(
      (a, b) =>
        b.swapsTogether - a.swapsTogether ||
        b.actsTogether - a.actsTogether ||
        b.winsTogether - a.winsTogether ||
        a.partner.localeCompare(b.partner)
    )[0];
  }, [partnerStats]);

  return (
    <Card
      style={{
        padding: '1rem',
        background:
          'radial-gradient(circle at 0% 0%, rgba(110,231,183,0.08), transparent 32%), radial-gradient(circle at 100% 100%, rgba(96,165,250,0.08), transparent 38%), var(--bg-secondary)',
      }}
    >
      <div style={{ marginBottom: '1rem' }}>
        <h3 style={{ margin: 0, marginBottom: '0.25rem' }}>Best Partner</h3>
        <p style={{ margin: 0, color: 'var(--text-secondary)' }}>
          Enter one racer to see who they have won with and swapped with the most.
        </p>
      </div>

      <label style={{ display: 'grid', gap: '0.35rem', marginBottom: '1rem' }}>
        <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>Player</span>
        <input
          list="best-partner-player-options"
          value={selectedPlayer}
          onChange={(event) => setSelectedPlayer(event.target.value)}
          placeholder="Enter a player name"
          style={{
            width: '100%',
            padding: '0.75rem 0.9rem',
            borderRadius: '10px',
            border: '1px solid var(--border)',
            background: 'var(--bg-primary)',
            color: 'var(--text-primary)',
          }}
        />
      </label>

      <datalist id="best-partner-player-options">
        {playerOptions.map((playerName) => (
          <option key={playerName} value={playerName} />
        ))}
      </datalist>

      {!normalizedSelectedPlayer ? (
        <div style={{ color: 'var(--text-secondary)', fontSize: '0.95rem' }}>
          Enter one name to see their most successful teammates.
        </div>
      ) : partnerStats.length === 0 ? (
        <div style={{ color: 'var(--text-secondary)', fontSize: '0.95rem' }}>
          No teammate history found for {selectedPlayerLabel || selectedPlayer.trim()}.
        </div>
      ) : (
        <>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
              gap: '0.75rem',
              marginBottom: '1rem',
            }}
          >
            <div
              style={{
                padding: '0.95rem 1rem',
                borderRadius: '12px',
                border: '1px solid rgba(74, 222, 128, 0.35)',
                background:
                  'linear-gradient(180deg, rgba(74, 222, 128, 0.10), rgba(255,255,255,0.03))',
              }}
            >
              <div
                style={{
                  fontSize: '0.75rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.06em',
                  color: 'var(--text-secondary)',
                  marginBottom: '0.45rem',
                }}
              >
                Most Wins With
              </div>
              <div style={{ fontSize: '1.45rem', fontWeight: 700, marginBottom: '0.35rem' }}>
                {topWinPartner?.partner}
              </div>
              <div style={{ fontSize: '0.88rem', color: 'var(--text-secondary)' }}>
                {topWinPartner?.winsTogether} wins across {topWinPartner?.actsTogether} ACT
                {topWinPartner?.actsTogether !== 1 ? 's' : ''}
              </div>
            </div>

            <div
              style={{
                padding: '0.95rem 1rem',
                borderRadius: '12px',
                border: '1px solid rgba(96, 165, 250, 0.35)',
                background:
                  'linear-gradient(180deg, rgba(96, 165, 250, 0.10), rgba(255,255,255,0.03))',
              }}
            >
              <div
                style={{
                  fontSize: '0.75rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.06em',
                  color: 'var(--text-secondary)',
                  marginBottom: '0.45rem',
                }}
              >
                Most Swaps With
              </div>
              <div style={{ fontSize: '1.45rem', fontWeight: 700, marginBottom: '0.35rem' }}>
                {topSwapPartner?.partner}
              </div>
              <div style={{ fontSize: '0.88rem', color: 'var(--text-secondary)' }}>
                {topSwapPartner?.swapsTogether} swaps across {topSwapPartner?.actsTogether} ACT
                {topSwapPartner?.actsTogether !== 1 ? 's' : ''}
              </div>
            </div>
          </div>

          <div
            style={{
              border: '1px solid var(--border)',
              borderRadius: '14px',
              overflow: 'hidden',
              background: 'rgba(255,255,255,0.02)',
            }}
          >
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'minmax(0, 1.6fr) repeat(3, minmax(88px, 0.7fr))',
                gap: '0.75rem',
                padding: '0.8rem 0.95rem',
                background: 'rgba(255,255,255,0.03)',
                borderBottom: '1px solid var(--border)',
                fontSize: '0.76rem',
                textTransform: 'uppercase',
                letterSpacing: '0.06em',
                color: 'var(--text-secondary)',
              }}
            >
              <div>Partner</div>
              <div>ACTs</div>
              <div>Wins</div>
              <div>Swaps</div>
            </div>

            {partnerStats.map((partner) => (
              <div
                key={partner.partner}
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'minmax(0, 1.6fr) repeat(3, minmax(88px, 0.7fr))',
                  gap: '0.75rem',
                  padding: '0.85rem 0.95rem',
                  borderBottom: '1px solid rgba(255,255,255,0.04)',
                  alignItems: 'center',
                }}
              >
                <div style={{ fontWeight: 600 }}>{partner.partner}</div>
                <div style={{ color: 'var(--text-secondary)' }}>{partner.actsTogether}</div>
                <div style={{ color: '#86efac', fontWeight: 600 }}>{partner.winsTogether}</div>
                <div style={{ color: '#93c5fd', fontWeight: 600 }}>{partner.swapsTogether}</div>
              </div>
            ))}
          </div>
        </>
      )}
    </Card>
  );
}

function Other() {
  const navigate = useNavigate();
  const [history, setHistory] = useState([]);

  useEffect(() => subscribeToActHistory(setHistory), []);

  const options = [
    {
      title: 'SAT History',
      description: 'Explore historical SAT data and insights.',
      icon: 'timeline-bar-chart',
      onClick: () => navigate('/sat-history'),
    },
  ];

  return (
    <div style={{ padding: '2rem', maxWidth: '900px', margin: '0 auto' }}>
      <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
        <H2 style={{ marginBottom: '0.5rem' }}>Other Tools</H2>
        <p style={{ color: 'var(--text-secondary)' }}>
          Access supporting tools and upcoming experiences for the ACT scorecard platform.
        </p>
      </div>

      <div style={{ marginBottom: '1.5rem' }}>
        <ChooserTool />
      </div>

      <div style={{ marginBottom: '1.5rem' }}>
        <VersusTool history={history} />
      </div>

      <div style={{ marginBottom: '1.5rem' }}>
        <OpponentPerformanceTool history={history} />
      </div>

      <div style={{ marginBottom: '1.5rem' }}>
        <BestPartnerTool history={history} />
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
          gap: '1.5rem',
        }}
      >
        {options.map((option) => (
          <Card
            key={option.title}
            interactive
            onClick={option.onClick}
            style={{
              padding: '1.75rem',
              cursor: 'pointer',
              display: 'flex',
              flexDirection: 'column',
              gap: '1rem',
            }}
          >
            <Icon icon={option.icon} size={28} style={{ color: 'var(--accent-primary)' }} />
            <div>
              <h3 style={{ margin: 0, marginBottom: '0.5rem' }}>{option.title}</h3>
              <p style={{ margin: 0, color: 'var(--text-secondary)' }}>{option.description}</p>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}

export default Other;
